
var Model = require("../types/DebouncedModel.js");
var debounce = require("debounce");

module.exports = Model({
	name: "managedcontactrequests",
	_who: null,
	_group: null,
	init: init,
	uninit: uninit,
	triggers: triggers,
	listeners: listeners,
	debounce: 100, // Debounce pushes by 50 milliseconds
	reset: reset,
	search_clear: search_clear,
});

/**
 * Creates arrays to receive data from 'managedincomingcontactrequests' and
 * 'managedoutgoingcontactrequests' models and pushes them to this model's data
 * object.
 * @return {[type]} [description]
 */
function init() {
	this._who = null;
	this._group = null;

	this.push({
		incoming: [],
		outgoing: [],
		search: [],
		denying: null,
		visible: true, //Flag for visibility (drop down) of contact lists
		isEmail: false
	});
}

function uninit() {
	this._who = null;
	this._group = null;
}

/**
 * These triggers from the browser UI initiate the basic functionality of the
 * 'managee' contact management
 * @return {[type]} [description]
 */
function triggers() {
	return {
		"toggle": handle_trigger_toggle,
		"search": debounce(handle_trigger_search, 1000), //.2 sec pause before search executes
		"search:clear": search_clear, //copied from ContactRequest
		"request": handle_trigger_initiate,
		"cancel": handle_trigger_cancel,
		"accept": handle_trigger_accept,
		"deny": handle_trigger_deny,
		"deny:confirm": handle_request_deny,
		"deny:clear": clear_denying,
		"view": view,
		"reset": trigger_reset,
	};
}

/**
 * Listen for updates to the lists of the incoming and outgoing contact
 * requests for the 'manageee'
 * @return {[type]} [description]
 */
function listeners() {
	return {
		"managedincomingcontactrequests:updated": handle_incoming_update,
		"managedoutgoingcontactrequests:updated": handle_outgoing_update,
	};
}

function view(data) {
	const {who, group} = data;

	if(who === this._who) return;
	this._who = who;
	this._group = group;

	this.emit("managedincomingcontactrequests:trigger:view", data);
	this.emit("managedoutgoingcontactrequests:trigger:view", data);
}

function trigger_reset() {
	this.emit("usermetrics:trigger:track", "close managed contact requests", {
		who: this._who,
	});
	this.reset();
	this.emit("managedincomingcontactrequests:trigger:clear");
	this.emit("managedoutgoingcontactrequests:trigger:clear");
}

/**
 * Resets all variables to the 'init' state.
 */
function reset() {
	this._who = null;
	this._group = null;

	this.push({
		_who: null,
		incoming: [],
		outgoing: [],
		search: [],
		visible: true,
		isEmail: false
	});
}

/**
 * Populates the 'incoming' variable with the array in the data of
 * the 'managedincomingcontactrequests' model when it updates.
 * @param  {Array} incoming List of user _id's making requests of 'managee'.
 * @return {Array}          List of user _id's making requests of 'managee'.
 */
function handle_incoming_update(incoming) {
	this.extend({
		incoming: incoming
	});
}

/**
 * Populates the 'outgoing' variable with the array in the data of
 * the 'managedoutgoingcontactrequests' model when it updates.
 * @param  {Array} outgoing List of user _id's for contact requests made by 'managee'.
 * @return {Array}          List of user _id's for contact requests made by 'managee'
 */
function handle_outgoing_update(outgoing) {
	this.extend({
		outgoing: outgoing
	});
}

/**
 * Is passed in a desired contact _id from UI, which is combined with the
 * 'managee' user _id to initiate a contact request.  It then clears the search
 * results.
 * @param  {String} who [description]
 * @return {String}     [description]
 */
function handle_trigger_initiate(data) {
	var managee = this._who;
	if (!managee) return;
	var who = data.who;
	const group = this._group;

	this.api.manage.contacts.requests
		.request(who, managee, group) // Make request
		.then(requested.bind(this))
		.catch(console.error.bind(console));

	this.emit("usermetrics:trigger:track", "initiate contact request for managed user", {
		who: this._who,
	});
}

function requested(request) {
	this.search_clear();
	this.dispatch("requested", request);

	this.dispatch("newcontactrequest", {
		who: this._who,
		section: "contacts",
		timestamp: new Date().toISOString(),
	});
	var message = "managedcontacts.requested";
	this.emit("toast:trigger:show", {
		content: message
	});
}

/**
 * Is passed a request _id from the UI that is coupled with the 'managee' _id to
 * cancel a contact request.
 * @param  {String} request Request _id
 */
async function handle_trigger_cancel(data) {
	var managee = this._who;

	if (!managee) return;
	var request = data.request;
	const group = this._group;

	await this.api.manage.contacts.requests
		.cancel(request, managee, group);

	var message = "managedcontacts.cancel";
	this.emit("toast:trigger:show", {
		content: message,
		style: "error"
	});

	this.emit("usermetrics:trigger:track", "cancel contact request for managed user", {
		who: this._who,
  });
	this.dispatch("newcontactrequest", {
		who: this._who,
		section: "contacts",
		timestamp: new Date().toISOString(),
	});
}

/**
 * Is passed a request _id from the UI that is coupled with the 'managee' _id to
 * accept a contact request.
 * @param  {String} request Request _id
 */
function handle_trigger_accept(data) {
	var managee = this._who;
	if (!managee) return;
	var request = data.request;
	const group = this._group;

	this.api.manage.contacts.requests
		.accept(request, managee, group);

	this.dispatch("newcontactrequest", {
		who: this._who,
		section: "contacts",
		timestamp: new Date().toISOString(),
	});

	var message = "managedcontacts.accept";
	this.emit("toast:trigger:show", {
		content: message
	});

	this.emit("usermetrics:trigger:track", "accept contact request for managed user", {
		who: this._who,
	});
}

function handle_trigger_deny(data) {
	var request = data.request;
	this.extend({
		denying: request
	});

	this.emit("usermetrics:trigger:track", "open deny contact request confirmation popup for managed user", {
		who: this._who,
	});
}

/**
	 * Is passed a request _id from the UI that is coupled with the 'managee' _id to
	 * deny a contact request.
	 * @param  {String} request Request _id
	 */
function handle_request_deny(data) {
	var managee = this._who;
	if (!managee) return;
	const group = this._group;
	var request = data.request;
	this.emit("save:trigger:start");
	this.api.manage.contacts.requests
		.deny(request, managee, group)
		.catch(console.error.bind(console))
		.then(handle_denied.bind(this));
	this.emit("usermetrics:trigger:track", "deny contact request for managed user", {
		who: this._who,
	});
}

function handle_denied() {
	this.emit("save:trigger:finish");
	this.dispatch("newcontactrequest", {
		who: this._who,
		section: "contacts",
		timestamp: new Date().toISOString(),
	});
	this.extend({
		denying: null
	});
}

/**
 * Takes in a query string from the UI and populates the 'search' array variable
 * with the results of the search.
 * @param  {String} query User entered query for a person.
 * @return {Array}       Populates 'search' variable
 */
function handle_trigger_search(data) {
	var query = data.query;
	if (!query) return;

	this.api.people
		.search(query)
		.then(filterOutSelf.bind(this))
		.then(handle_search_results.bind(this, query))
		.catch(console.error.bind(console));
	this.emit("usermetrics:trigger:track", "search for user in managed user's contact requests", {
		who: this._who,
	});
}

function filterOutSelf(results) {
	var who = this._who;
	return results.filter(function(result){
		return result._id !== who;
	});
}

/**
 * Populates the 'search' array variable.
 * @param  {Array} results Array of user _id's matchine a UI query
 * @return {Array} search  Array of user _id's
 */
function handle_search_results(query, results) {
	var re = /\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;
	var isEmail = (query.match(re)) !== null;

	this.extend({
		query: query,
		search: results,
		isEmail: isEmail
	});
}

/**
 * Clears search results.
 * @return {[type]} [description]
 */
function search_clear() {
	this.extend({
		search: [],
		isEmail: false
	});
}

/**
 * Toggles visibility.  Default is true.
 * @return {[type]} [description]
 */
function handle_trigger_toggle() {
	// original; this.data.visible = !this.data.visible;
	this.extend({
		visible: !this.data.visible,
	});
	this.emit("usermetrics:trigger:track", "toggle managed user's visibility to contact requests", {
		who: this._who,
	});
}

function clear_denying() {
	this.extend({
		denying: null
	});
	this.emit("usermetrics:trigger:track", "close deny contact request confirmation popup for managed user", {
		who: this._who,
	});
}
